import React from 'react';
import { GenericCard, ModalHeader } from '@/components/dashboard';
import { Button, Icon, Separator, Typography } from '@/components/common';
import { GenericBadge, GenericBadgeColors } from '@/components/common/badge';
import { CaseDetailsLawyer, Option, StatusTypes } from '@/interfaces';
import { formatErrorMessage, monthsSince } from '@/lib';
import { AvatarGroup } from '@/components/common/avatar/avatarGroup';
import { END_STATUSES_CASE } from '@/const/cases';
import useModal from '@/hooks/useModal';
import { InputList } from '@/components/form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
	casesLawyerService,
	casesService,
	GET_CASE_STEPS_STATUS,
	GET_CASE_TYPES,
	GET_LAWYER_CASE_DETAILS,
} from '@/api';
import { FieldValues, useForm } from 'react-hook-form';
import { useEndOrDeleteCase, useNotification } from '@/hooks';
import Icons from '@/const/icons';
import {
	END_OR_DELETE_CASE_MODAL_SHELL,
	EndOrDeleteCaseModal,
	EndOrDeleteCaseModalTitle,
} from './EndOrDeleteCaseModal';

interface Props {
	caseDetails?: CaseDetailsLawyer;
}

export const CaseInformation: React.FC<Props> = ({ caseDetails }) => {
	const { Modal: ModalCaseType, show, hide } = useModal();
	const { register, handleSubmit, setValue, watch } = useForm();
	const { toastSuccess, toastError } = useNotification();
	const queryClient = useQueryClient();
	const {
		hasClients,
		isEnded,
		isDisabled: isEndOrDeleteDisabled,
		isSubmitting,
		isOpen: isConfirmOpen,
		Modal: ModalConfirmation,
		register: registerEndOrDelete,
		open: openEndOrDeleteModal,
		close: closeEndOrDeleteModal,
		confirm: handleEndOrDelete,
		caseTitle,
	} = useEndOrDeleteCase({
		caseId: caseDetails?.id,
		caseTitle: caseDetails?.title ?? '',
		clientCount: caseDetails?.number_of_client ?? 0,
		progressName: caseDetails?.progress_name,
		redirectOnSuccess: true,
	});

	// Types
	const { data: types } = useQuery({
		queryKey: [GET_CASE_TYPES],
		queryFn: () => casesService.getTypes(),
	});

	const listCaseTypes = React.useMemo(() => {
		const list: Option[] = [
			{
				label: 'Select',
				name: '',
				placeholder: true,
			},
		];

		if (types?.data.items) {
			types.data.items.forEach((type) => {
				list.push({
					label: type.name,
					name: type.id.toString(),
				});
			});
		}

		return list;
	}, [types]);

	const initialCaseType = React.useMemo(() => {
		return listCaseTypes?.find(({ name }) => caseDetails?.case_type === name);
	}, [listCaseTypes, caseDetails?.case_type]);

	const onSubmit = async (values: FieldValues) => {
		if (!caseDetails) return;
		try {
			const data = {
				caseType: values.caseType,
				caseName: caseDetails.title,
				statusId: caseDetails.status_id,
				categoryId: caseDetails.category_id,
				elegibleStateIds: Array.isArray(caseDetails?.states)
					? Array.from(
							new Set<number>(
								caseDetails.states.map((state) => Number(state.id))
							)
					  )
					: [],
				brandsData: caseDetails.brands.map((b) => ({
					caseBrandId: b.caseBrandId,
					brandId: b.id,
					entityType: b.entity_type,
					defendantIncorporation: b.defendant_incorporation.id
						? Number(b.defendant_incorporation.id)
						: 0,
					princpialPlaceOfBusiness: b.principal_place_of_business.name
						? Number(b.principal_place_of_business.name)
						: 0,
				})),
				lawyerIds: caseDetails.lawyers
					? caseDetails.lawyers.map((l) => l.id.toString())
					: [],
				caseId: caseDetails.id,
			};
			await casesLawyerService.complete(data);
			toastSuccess('The case type has been successfully changed');
			queryClient.invalidateQueries({
				queryKey: [GET_LAWYER_CASE_DETAILS, caseDetails.id],
			});
			queryClient.refetchQueries({
				queryKey: [GET_CASE_STEPS_STATUS, caseDetails.id],
			});
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			hide();
		}
	};

	const getStatusValues = () => {
		let status = {
			label: 'Setup',
			color: 'orange',
		};
		if (!caseDetails?.is_draft) {
			status = {
				label: 'Live',
				color: 'green',
			};
		}
		if (END_STATUSES_CASE.includes(caseDetails?.progress_name as StatusTypes)) {
			status = {
				label: 'Closed',
				color: 'red',
			};
		}
		return status;
	};

	const { label, color } = getStatusValues();
	const currentCaseType = watch('caseType');
	const caseType = caseDetails?.case_type
		? caseDetails.case_type.replaceAll('_', ' ').toLowerCase()
		: '';

	return (
		<>
			<GenericCard className="w-80 p-8 border border-green-secondary !min-h-fit md:h-auto">
				<Typography type="regular-18" className="text-black mb-6">
					Case Information
				</Typography>
				<div className="space-y-3">
					<Row
						label="Status"
						value={
							<GenericBadge
								label={label}
								colorStatus={color as GenericBadgeColors}
							/>
						}
					/>
					<Row
						label="Case Type"
						value={<CaseType label={caseType} onClick={() => show()} />}
					/>
					<Row label="Case Category" value={caseDetails?.category?.name} />
					<Row
						label="Participating customers"
						value={`${caseDetails?.number_of_client ?? 0}`}
					/>
					<Row
						label="Total length of the case"
						value={monthsSince(caseDetails?.created_at ?? '')}
					/>
					<Row
						label="Assigned Lawyers"
						value={
							<AvatarGroup
								options={
									caseDetails?.lawyers?.map((l) => ({
										url: l.profile_picture ?? undefined,
										initials: l.username
											.split(' ')
											.map((n) => n.at(0))
											.join(''),
									})) ?? []
								}
							/>
						}
					/>
				</div>
				{!isEnded && (
					<>
						<Separator className="my-3" />
						<div className="flex justify-end">
							<Button
								decoration="danger-outline"
								size="medium"
								type="button"
								disabled={isEndOrDeleteDisabled}
								onClick={openEndOrDeleteModal}
								className="!h-[30px]"
							>
								{hasClients ? 'End Case' : 'Delete Case'}
							</Button>
						</div>
					</>
				)}
			</GenericCard>
			<ModalCaseType
				withXButton
				backgroundColor="white"
				classNameCardContainer="!w-[532px] bg-white"
				title={<ModalHeader title={'Change Case Type'} />}
			>
				<form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
					{listCaseTypes && (
						<InputList
							// disabled={isCaseReadOnly}
							myDefaultValue={initialCaseType?.name}
							name="caseType"
							title="Case Type"
							register={register}
							classNameInput="!mt-2"
							classNameTitle="!text-[16px]"
							classNameLabel="!text-[16px]"
							options={listCaseTypes}
							setValueInput={setValue}
						/>
					)}
					<div className="text-left">
						<Typography type="regular-16" className="break-after-all">
							Changing the case type will update document requirements:
						</Typography>
						<ol className="mt-6">
							<li>
								<Typography type="regular-16">
									<span className="font-semibold">Class Action</span>
									{` → `}
									Complaints required
								</Typography>
							</li>
							<li>
								<Typography type="regular-16">
									<span className="font-semibold">Mass Action</span>
									{` → `}
									Demand letter required
								</Typography>
							</li>
						</ol>
						<Typography type="regular-16" className="break-after-all mt-6">
							Existing documents will not be affected.
						</Typography>
					</div>
					<div className="flex justify-center gap-2">
						<Button decoration="secondary" size="fit" onClick={() => hide()}>
							Cancel
						</Button>
						<Button
							disabled={caseDetails?.case_type === currentCaseType}
							type="submit"
							decoration="primary"
							size="fit"
						>
							Confirm
						</Button>
					</div>
				</form>
			</ModalCaseType>
			<ModalConfirmation
				{...END_OR_DELETE_CASE_MODAL_SHELL}
				title={<EndOrDeleteCaseModalTitle hasClients={hasClients} />}
				withXButton={!isSubmitting}
				avoidClose={isSubmitting}
				onClose={closeEndOrDeleteModal}
			>
				<EndOrDeleteCaseModal
					key={isConfirmOpen ? 'end-or-delete-open' : 'end-or-delete-closed'}
					hasClients={hasClients}
					caseTitle={caseTitle}
					register={registerEndOrDelete}
					onConfirm={handleEndOrDelete}
					onClose={closeEndOrDeleteModal}
					isSubmitting={isSubmitting}
				/>
			</ModalConfirmation>
		</>
	);
};

interface RowProps {
	label: string;
	value: React.ReactNode | string;
}

const Row: React.FC<RowProps> = ({ label, value }) => {
	return (
		<div className="flex items-center justify-between">
			<Typography type="regular-12" className="text-gray-35">
				{label}:
			</Typography>
			<div>
				{typeof value === 'string' ? (
					<Typography type="regular-12">{value}</Typography>
				) : (
					value
				)}
			</div>
		</div>
	);
};

interface CaseTypeProps {
	label: string;
	onClick: () => void;
}

const CaseType: React.FC<CaseTypeProps> = ({ label, onClick }) => {
	return (
		<div className="flex flex-col justify-end">
			<Typography type="regular-12" className="text-gray-35 capitalize">
				{label}
			</Typography>
			<button className="flex justify-end items-center gap-1" onClick={onClick}>
				<Icon src={Icons.refresh} className="!size-3 !text-primary" />
				<Typography type="semibold-12" className="text-primary">
					Change
				</Typography>
			</button>
		</div>
	);
};
